/**
 * Email layout definitions, default content factories, and HTML render functions.
 *
 * Each layout produces table-based inner-body HTML.
 * The backend's wrap_with_branded_footer() adds the school logo header,
 * white container shell, and branded footer around this content.
 */

// Helpers

/**
 * Convert Quill alignment classes to inline styles and sanitize HTML for email.
 */
function emailHtml(html) {
  if (!html) return '';
  return html
    .replace(/class="ql-align-center"/g, 'style="text-align:center;"')
    .replace(/class="ql-align-right"/g, 'style="text-align:right;"')
    .replace(/class="ql-align-justify"/g, 'style="text-align:justify;"');
}

function getBrandPrimary() {
  if (typeof document !== 'undefined') {
    var val = getComputedStyle(document.documentElement).getPropertyValue('--primary-color');
    if (val && val.trim()) return val.trim();
  }
  return '#3b86f5';
}

function renderButton(button) {
  if (!button || !button.text) return '';
  var color = button.color || getBrandPrimary();
  var url = button.url || '#';
  return ''
    + '<table border="0" cellpadding="0" cellspacing="0" width="100%" style="margin:24px 0;">'
    + '<tr><td align="center">'
    + '<a href="' + url + '" target="_blank" style="'
    + 'display:inline-block;padding:12px 32px;'
    + 'background-color:' + color + ';color:#ffffff;'
    + 'text-decoration:none;border-radius:6px;'
    + 'font-family:Arial,Helvetica,sans-serif;font-size:16px;font-weight:bold;'
    + '">' + button.text + '</a>'
    + '</td></tr></table>';
}

var FONT_STACK = "font-family:Arial,Helvetica,sans-serif;";
var BODY_STYLE = FONT_STACK + "font-size:16px;line-height:1.6;color:#333333;";

// Layout Metadata

export var LAYOUTS = [
  {
    id: 'clean',
    name: 'Clean',
    description: 'Simple body text with an optional call-to-action button.',
    useCases: ['General', 'Follow Up'],
    slots: ['body', 'button'],
  },
  {
    id: 'hero',
    name: 'Hero',
    description: 'Large hero image at the top, body text, and a button.',
    useCases: ['Marketing', 'Enrollment'],
    slots: ['hero_image', 'body', 'button'],
  },
  {
    id: 'event',
    name: 'Event',
    description: 'Hero image with event details: name, date, time, and location.',
    useCases: ['Marketing', 'Enrollment'],
    slots: ['hero_image', 'event_name', 'event_date', 'event_time', 'event_location', 'body', 'button'],
  },
  {
    id: 'newsletter',
    name: 'Newsletter',
    description: 'Heading plus 1–4 content sections, each with optional image.',
    useCases: ['General', 'Marketing'],
    slots: ['heading', 'sections'],
  },
  {
    id: 'welcome',
    name: 'Welcome',
    description: 'Warm greeting with key highlights list and a button.',
    useCases: ['Enrollment'],
    slots: ['heading', 'body', 'highlights', 'button'],
  },
];

// Default Content Factories

export function getDefaultContent(layoutId) {
  switch (layoutId) {
    case 'clean':
      return {
        body: '<p>Hello {{FIRST_NAME}},</p><p>Write your message here.</p>',
        button: { text: '', url: '', color: '' },
      };
    case 'hero':
      return {
        hero_image: '',
        body: '<p>Hello {{FIRST_NAME}},</p><p>Write your message here.</p>',
        button: { text: 'Learn More', url: '', color: '' },
      };
    case 'event':
      return {
        hero_image: '',
        event_name: '',
        event_date: '',
        event_time: '',
        event_location: '',
        body: '<p>We\'d love to see you there!</p>',
        button: { text: 'RSVP Now', url: '', color: '' },
      };
    case 'newsletter':
      return {
        heading: '',
        sections: [
          { image: '', heading: '', body: '<p>Write section content here.</p>' },
        ],
      };
    case 'welcome':
      return {
        heading: 'Welcome!',
        body: '<p>Hello {{FIRST_NAME}},</p><p>We\'re excited to have you join us.</p>',
        highlights: [''],
        button: { text: 'Get Started', url: '', color: '' },
      };
    default:
      return {};
  }
}

// Pre-designed Templates

export var TEMPLATES = [

  // LEAD TEMPLATES

  {
    id: 'tpl-new-inquiry-welcome',
    name: 'New Inquiry Welcome',
    description: 'Welcome a new lead who just submitted an inquiry.',
    tags: ['Lead', 'New Inquiry'],
    layout: 'clean',
    content: {
      body: '<p>Hello {{FIRST_NAME}},</p><p>Thank you for your interest in {{SCHOOL_NAME}}! We received your inquiry and are excited to help you explore your options in healthcare training.</p><p>One of our admissions team members will be reaching out to you shortly. In the meantime, feel free to reply to this email with any questions.</p><p>We look forward to speaking with you!</p><p>Best regards,<br/>{{SCHOOL_NAME}} Admissions</p>',
      button: { text: 'View Our Programs', url: '', color: '' },
    },
  },
  {
    id: 'tpl-lead-follow-up',
    name: 'Lead Follow Up',
    description: 'Follow up after initial contact with a prospective student.',
    tags: ['Lead', 'Contacted'],
    layout: 'clean',
    content: {
      body: '<p>Hello {{FIRST_NAME}},</p><p>Thank you for taking the time to speak with us. We enjoyed learning more about your goals and how we can help you start your healthcare career.</p><p>If you have any questions or need additional information about our programs, please don\'t hesitate to reach out. We\'re here to help!</p><p>Best regards,<br/>{{SCHOOL_NAME}}</p>',
      button: { text: '', url: '', color: '' },
    },
  },
  {
    id: 'tpl-info-session-invite',
    name: 'Info Session Invite',
    description: 'Invite a lead to attend an upcoming information session.',
    tags: ['Lead', 'Engaged', 'Event'],
    layout: 'event',
    content: {
      hero_image: '',
      event_name: 'Information Session',
      event_date: '',
      event_time: '',
      event_location: '',
      body: '<p>Hello {{FIRST_NAME}},</p><p>We\'d love for you to join us at an upcoming information session! Learn about our healthcare training programs, meet our instructors, tour the campus, and get all your questions answered.</p><p>Space is limited, so be sure to reserve your spot.</p>',
      button: { text: 'Reserve My Spot', url: '', color: '' },
    },
  },
  {
    id: 'tpl-info-session-reminder',
    name: 'Info Session Reminder',
    description: 'Remind a lead about their upcoming info session.',
    tags: ['Lead', 'Info Session Booked'],
    layout: 'clean',
    content: {
      body: '<p>Hello {{FIRST_NAME}},</p><p>Just a friendly reminder that your information session is coming up! We\'re looking forward to meeting you and showing you everything {{SCHOOL_NAME}} has to offer.</p><p>If you need to reschedule or have any questions, simply reply to this email.</p><p>See you soon!</p><p>{{SCHOOL_NAME}} Admissions</p>',
      button: { text: '', url: '', color: '' },
    },
  },
  {
    id: 'tpl-info-session-followup',
    name: 'Post Info Session Follow Up',
    description: 'Follow up after a lead attends an info session.',
    tags: ['Lead', 'Info Session Attended'],
    layout: 'clean',
    content: {
      body: '<p>Hello {{FIRST_NAME}},</p><p>Thank you for attending our information session! We hope you found it helpful and informative.</p><p>As a next step, we encourage you to begin your application. Our admissions team is here to guide you through the process and answer any questions you may have.</p><p>Don\'t wait too long \u2014 class sizes are limited and spots fill up quickly!</p><p>Best regards,<br/>{{SCHOOL_NAME}} Admissions</p>',
      button: { text: 'Start My Application', url: '', color: '' },
    },
  },
  {
    id: 'tpl-application-started',
    name: 'Application Started',
    description: 'Encourage a lead who started but hasn\'t completed their application.',
    tags: ['Lead', 'Application Started'],
    layout: 'clean',
    content: {
      body: '<p>Hello {{FIRST_NAME}},</p><p>We noticed you\'ve started your application to {{SCHOOL_NAME}} \u2014 great first step! You\'re almost there.</p><p>If you need help completing any section or have questions about the requirements, our admissions team is ready to assist you.</p><p>Finish your application today and take the next step toward your healthcare career!</p>',
      button: { text: 'Complete My Application', url: '', color: '' },
    },
  },
  {
    id: 'tpl-application-received',
    name: 'Application Received',
    description: 'Confirm receipt of a completed application.',
    tags: ['Lead', 'Application Completed'],
    layout: 'clean',
    content: {
      body: '<p>Hello {{FIRST_NAME}},</p><p>We\'ve received your completed application to {{SCHOOL_NAME}}. Thank you!</p><p>Our admissions team will review your application and follow up with you within the next few business days. If we need any additional information, we\'ll be in touch.</p><p>In the meantime, if you have any questions, don\'t hesitate to reach out.</p><p>Best regards,<br/>{{SCHOOL_NAME}} Admissions</p>',
      button: { text: '', url: '', color: '' },
    },
  },
  {
    id: 'tpl-approved',
    name: 'Approval Notification',
    description: 'Notify a lead that their application has been approved.',
    tags: ['Lead', 'Approved'],
    layout: 'welcome',
    content: {
      heading: 'You\'ve Been Approved!',
      body: '<p>Hello {{FIRST_NAME}},</p><p>Congratulations! We\'re pleased to inform you that your application to {{SCHOOL_NAME}} has been approved.</p><p>Here are your next steps to complete the enrollment process:</p>',
      highlights: ['Review and sign your enrollment agreement', 'Complete payment or set up a payment plan', 'Submit any remaining documents', 'Attend your orientation session'],
      button: { text: 'Complete Enrollment', url: '', color: '' },
    },
  },
  {
    id: 'tpl-payment-reminder',
    name: 'Payment Reminder',
    description: 'Remind a lead about a pending payment to complete enrollment.',
    tags: ['Lead', 'Payment Pending'],
    layout: 'clean',
    content: {
      body: '<p>Hello {{FIRST_NAME}},</p><p>You\'re so close to completing your enrollment at {{SCHOOL_NAME}}! We just need your payment to finalize everything.</p><p>If you have questions about payment options or need to set up a payment plan, please don\'t hesitate to contact us. We\'re happy to help.</p><p>Best regards,<br/>{{SCHOOL_NAME}} Admissions</p>',
      button: { text: 'Make Payment', url: '', color: '' },
    },
  },
  {
    id: 'tpl-re-engagement',
    name: 'Re-Engagement',
    description: 'Re-engage a lead who has gone cold or stopped responding.',
    tags: ['Lead', 'Cold'],
    layout: 'clean',
    content: {
      body: '<p>Hello {{FIRST_NAME}},</p><p>It\'s been a while since we last connected, and we wanted to check in. We understand that life gets busy, and choosing the right training program is an important decision.</p><p>At {{SCHOOL_NAME}}, we\'re still here to help you pursue your healthcare career whenever you\'re ready. New classes are starting soon, and we\'d love to have you join us.</p><p>Would you like to schedule a quick call to discuss your options?</p><p>Best regards,<br/>{{SCHOOL_NAME}} Admissions</p>',
      button: { text: 'Schedule a Call', url: '', color: '' },
    },
  },
  {
    id: 'tpl-open-house',
    name: 'Open House Invite',
    description: 'Invite prospective students to an open house event.',
    tags: ['Lead', 'Marketing', 'Event'],
    layout: 'event',
    content: {
      hero_image: '',
      event_name: 'Open House',
      event_date: '',
      event_time: '',
      event_location: '',
      body: '<p>Hello {{FIRST_NAME}},</p><p>You\'re invited to our upcoming Open House! Come tour the campus, meet instructors, and learn about our healthcare training programs.</p><p>We\'d love to see you there!</p>',
      button: { text: 'RSVP Now', url: '', color: '' },
    },
  },

  // STUDENT TEMPLATES

  {
    id: 'tpl-enrolled-letter',
    name: 'Enrolled Letter',
    description: 'Welcome a newly enrolled student and outline what to expect.',
    tags: ['Student', 'Enrolled'],
    layout: 'welcome',
    content: {
      heading: 'Welcome to {{SCHOOL_NAME}}!',
      body: '<p>Hello {{FIRST_NAME}},</p><p>Congratulations on your enrollment! We\'re thrilled to have you join our healthcare training program. Here\'s what you need to know to get started:</p>',
      highlights: ['Attend your scheduled orientation', 'Review the student handbook', 'Gather required clinical supplies', 'Set up your student portal account', 'Complete any remaining health screenings'],
      button: { text: 'Access Student Portal', url: '', color: '' },
    },
  },
  {
    id: 'tpl-orientation-reminder',
    name: 'Orientation Reminder',
    description: 'Remind a new student about their upcoming orientation.',
    tags: ['Student', 'Enrolled'],
    layout: 'event',
    content: {
      hero_image: '',
      event_name: 'New Student Orientation',
      event_date: '',
      event_time: '',
      event_location: '',
      body: '<p>Hello {{FIRST_NAME}},</p><p>Your orientation is coming up! This is a required session where you\'ll meet your instructors, tour the facilities, and learn everything you need to succeed in your program.</p><p>Please bring a valid photo ID and arrive 15 minutes early.</p>',
      button: { text: '', url: '', color: '' },
    },
  },
  {
    id: 'tpl-class-starting',
    name: 'Class Starting Soon',
    description: 'Notify a student that their class is about to begin.',
    tags: ['Student', 'Enrolled'],
    layout: 'clean',
    content: {
      body: '<p>Hello {{FIRST_NAME}},</p><p>Your classes at {{SCHOOL_NAME}} are starting soon! We\'re excited to see you in the classroom.</p><p>Please make sure you have all your required materials, including scrubs, a stethoscope, and your textbooks. If you\'re unsure about what you need, contact our office and we\'ll help you out.</p><p>Here\'s to the start of your healthcare career!</p><p>{{SCHOOL_NAME}}</p>',
      button: { text: '', url: '', color: '' },
    },
  },
  {
    id: 'tpl-clinical-rotation-details',
    name: 'Clinical Rotation Details',
    description: 'Provide details about an upcoming clinical rotation assignment.',
    tags: ['Student', 'In Progress', 'Healthcare'],
    layout: 'welcome',
    content: {
      heading: 'Clinical Rotation Assignment',
      body: '<p>Hello {{FIRST_NAME}},</p><p>Your clinical rotation has been scheduled! Please review the details below carefully and reach out if you have any questions.</p>',
      highlights: ['Arrive 15 minutes before your shift', 'Wear clean scrubs and closed-toe shoes', 'Bring your student ID and clinical badge', 'Complete all pre-clinical requirements'],
      button: { text: '', url: '', color: '' },
    },
  },
  {
    id: 'tpl-student-check-in',
    name: 'Student Check-In',
    description: 'Check in with a student midway through their program.',
    tags: ['Student', 'In Progress'],
    layout: 'clean',
    content: {
      body: '<p>Hello {{FIRST_NAME}},</p><p>We wanted to check in and see how things are going with your program at {{SCHOOL_NAME}}. You\'re making great progress!</p><p>If you\'re facing any challenges or need additional support, please don\'t hesitate to reach out. We\'re here to help you succeed.</p><p>Keep up the great work!</p><p>{{SCHOOL_NAME}}</p>',
      button: { text: '', url: '', color: '' },
    },
  },
  {
    id: 'tpl-exam-prep-reminder',
    name: 'Certification Exam Prep',
    description: 'Remind students to prepare for their certification exam.',
    tags: ['Student', 'Completed', 'Healthcare'],
    layout: 'welcome',
    content: {
      heading: 'Certification Exam Preparation',
      body: '<p>Hello {{FIRST_NAME}},</p><p>You\'re nearing the finish line! It\'s time to start preparing for your certification exam. Here are some tips to help you succeed:</p>',
      highlights: ['Review all classroom notes and study guides', 'Complete practice exams and quizzes', 'Attend any available review sessions', 'Get plenty of rest before exam day', 'Bring required identification to the testing site'],
      button: { text: 'Access Study Materials', url: '', color: '' },
    },
  },
  {
    id: 'tpl-graduation-congrats',
    name: 'Graduation Congratulations',
    description: 'Congratulate a student on completing their program.',
    tags: ['Student', 'Graduated'],
    layout: 'hero',
    content: {
      hero_image: '',
      body: '<p>Hello {{FIRST_NAME}},</p><p>Congratulations on graduating from {{SCHOOL_NAME}}! You should be incredibly proud of this achievement.</p><p>As you move forward in your healthcare career, remember that we\'re always here to support you. Stay connected with us for job placement assistance and continuing education opportunities.</p><p>Wishing you all the best!</p><p>{{SCHOOL_NAME}}</p>',
      button: { text: 'View Job Opportunities', url: '', color: '' },
    },
  },
  {
    id: 'tpl-certification-congrats',
    name: 'Certification Achieved',
    description: 'Congratulate a student who passed their certification exam.',
    tags: ['Student', 'Certified', 'Healthcare'],
    layout: 'hero',
    content: {
      hero_image: '',
      body: '<p>Hello {{FIRST_NAME}},</p><p>We are thrilled to congratulate you on passing your certification exam! This is a major milestone in your healthcare career, and you should be very proud.</p><p>Your certification opens doors to rewarding opportunities in healthcare. Our placement team is ready to help you find the right position.</p><p>Congratulations again!</p><p>{{SCHOOL_NAME}}</p>',
      button: { text: 'Connect with Our Placement Team', url: '', color: '' },
    },
  },
  {
    id: 'tpl-job-placement-update',
    name: 'Job Placement Update',
    description: 'Share job placement resources and opportunities with graduates.',
    tags: ['Student', 'Graduated', 'Placed'],
    layout: 'newsletter',
    content: {
      heading: 'Job Placement Opportunities',
      sections: [
        { image: '', heading: 'Open Positions', body: '<p>Here are the latest healthcare job openings from our employer partners. Review the listings and let us know which ones interest you.</p>' },
        { image: '', heading: 'Career Resources', body: '<p>Need help with your resume, interview prep, or job search strategy? Our placement team is here to support you every step of the way.</p>' },
      ],
    },
  },
  {
    id: 'tpl-on-leave-check-in',
    name: 'On Leave Check-In',
    description: 'Check in with a student who is on leave from their program.',
    tags: ['Student', 'On Leave'],
    layout: 'clean',
    content: {
      body: '<p>Hello {{FIRST_NAME}},</p><p>We hope you\'re doing well. We understand you needed to take some time away from your program at {{SCHOOL_NAME}}, and we want you to know we\'re here for you.</p><p>When you\'re ready to return, our team will help make the transition as smooth as possible. New classes are starting regularly, and there may be flexible options to fit your schedule.</p><p>Feel free to reach out anytime \u2014 we\'d love to hear from you.</p><p>Best regards,<br/>{{SCHOOL_NAME}}</p>',
      button: { text: 'Contact Us', url: '', color: '' },
    },
  },
  {
    id: 'tpl-alumni-newsletter',
    name: 'Alumni Newsletter',
    description: 'Monthly newsletter for alumni with news and opportunities.',
    tags: ['Student', 'Alumni'],
    layout: 'newsletter',
    content: {
      heading: 'Alumni Update',
      sections: [
        { image: '', heading: 'School News', body: '<p>Stay connected with the latest updates from {{SCHOOL_NAME}}, including new programs, campus improvements, and upcoming events.</p>' },
        { image: '', heading: 'Alumni Spotlight', body: '<p>Celebrating the achievements of our graduates. Know someone doing great things? Let us know!</p>' },
        { image: '', heading: 'Continuing Education', body: '<p>Advance your career with our continuing education courses and certification programs.</p>' },
      ],
    },
  },

  // EMPLOYER TEMPLATES

  {
    id: 'tpl-employer-intro',
    name: 'Employer Introduction',
    description: 'Introduce your school and propose a hiring partnership.',
    tags: ['Employer', 'Prospect'],
    layout: 'clean',
    content: {
      body: '<p>Hello {{FIRST_NAME}},</p><p>My name is [Your Name] from {{SCHOOL_NAME}}. We train certified healthcare professionals and are always looking to connect our graduates with quality employers like your organization.</p><p>Our graduates are job-ready, having completed hands-on clinical rotations and passed their certification exams. We\'d love to explore a partnership to help fill your open healthcare positions.</p><p>Would you be available for a brief call this week?</p><p>Best regards,<br/>{{SCHOOL_NAME}}</p>',
      button: { text: '', url: '', color: '' },
    },
  },
  {
    id: 'tpl-employer-partnership-proposal',
    name: 'Partnership Proposal',
    description: 'Propose a formal hiring or clinical partnership with an employer.',
    tags: ['Employer', 'Partnership Discussion'],
    layout: 'welcome',
    content: {
      heading: 'Partnership Opportunity',
      body: '<p>Hello {{FIRST_NAME}},</p><p>Thank you for your interest in partnering with {{SCHOOL_NAME}}. We\'d like to outline the benefits of working together:</p>',
      highlights: ['Access to a pipeline of certified, job-ready healthcare graduates', 'Customized training aligned with your facility\'s needs', 'Clinical rotation hosting opportunities', 'Priority access to top-performing students', 'Ongoing support from our placement team'],
      button: { text: 'Schedule a Meeting', url: '', color: '' },
    },
  },
  {
    id: 'tpl-employer-clinical-site',
    name: 'Clinical Site Request',
    description: 'Request to use a facility as a clinical rotation site.',
    tags: ['Employer', 'Clinical Partner', 'Healthcare'],
    layout: 'clean',
    content: {
      body: '<p>Hello {{FIRST_NAME}},</p><p>I\'m reaching out from {{SCHOOL_NAME}} regarding a potential clinical site partnership. We\'re seeking quality healthcare facilities where our students can complete their required clinical rotations under supervised instruction.</p><p>Our students are well-prepared, professional, and eager to learn. We handle all background checks, health screenings, and liability insurance.</p><p>Would your facility be open to hosting our students? I\'d love to discuss the details further.</p><p>Best regards,<br/>{{SCHOOL_NAME}}</p>',
      button: { text: 'Learn More', url: '', color: '' },
    },
  },
  {
    id: 'tpl-employer-hiring-update',
    name: 'Graduates Available',
    description: 'Notify employer partners about newly available graduates for hire.',
    tags: ['Employer', 'Hiring', 'Active Partner'],
    layout: 'clean',
    content: {
      body: '<p>Hello {{FIRST_NAME}},</p><p>We have a new cohort of certified healthcare graduates from {{SCHOOL_NAME}} who are ready to join the workforce. These candidates have completed their training and clinical hours, and many have already passed their certification exams.</p><p>If you have any open positions, we\'d love to connect you with our top candidates. Let us know and our placement team will send over their profiles.</p><p>Best regards,<br/>{{SCHOOL_NAME}} Career Services</p>',
      button: { text: 'View Candidates', url: '', color: '' },
    },
  },
  {
    id: 'tpl-employer-thank-you',
    name: 'Employer Thank You',
    description: 'Thank an employer partner after a successful placement.',
    tags: ['Employer', 'Placement Complete'],
    layout: 'clean',
    content: {
      body: '<p>Hello {{FIRST_NAME}},</p><p>Thank you for hiring one of our {{SCHOOL_NAME}} graduates! We truly appreciate your partnership and trust in our training programs.</p><p>We\'d love to hear how things are going. Your feedback helps us continuously improve our curriculum and better prepare future graduates for success.</p><p>We look forward to continuing to work with you!</p><p>Best regards,<br/>{{SCHOOL_NAME}} Career Services</p>',
      button: { text: '', url: '', color: '' },
    },
  },

  // MARKETING / GENERAL TEMPLATES

  {
    id: 'tpl-new-program-announcement',
    name: 'New Program Announcement',
    description: 'Announce a new healthcare training program.',
    tags: ['Marketing', 'General'],
    layout: 'hero',
    content: {
      hero_image: '',
      body: '<p>Hello {{FIRST_NAME}},</p><p>We\'re excited to announce a brand new program at {{SCHOOL_NAME}}! This program is designed to prepare you for a rewarding career in healthcare.</p><p>Class sizes are limited, so secure your spot early. Contact our admissions team to learn more about program details, schedule, and enrollment requirements.</p>',
      button: { text: 'Learn More', url: '', color: '' },
    },
  },
  {
    id: 'tpl-new-class-starting',
    name: 'New Class Starting',
    description: 'Promote upcoming class start dates to prospective students.',
    tags: ['Marketing', 'Lead'],
    layout: 'hero',
    content: {
      hero_image: '',
      body: '<p>Hello {{FIRST_NAME}},</p><p>New classes are starting soon at {{SCHOOL_NAME}}! Now is the perfect time to take the first step toward your healthcare career.</p><p>Our programs offer hands-on training, experienced instructors, and career placement support. Don\'t miss this opportunity \u2014 spots are limited!</p>',
      button: { text: 'Enroll Today', url: '', color: '' },
    },
  },
  {
    id: 'tpl-monthly-newsletter',
    name: 'Monthly Newsletter',
    description: 'A monthly newsletter with school news and updates.',
    tags: ['Marketing', 'General'],
    layout: 'newsletter',
    content: {
      heading: 'Monthly Update',
      sections: [
        { image: '', heading: 'Campus News', body: '<p>Share the latest happenings on campus, upcoming events, and announcements.</p>' },
        { image: '', heading: 'Student Spotlight', body: '<p>Highlight a student achievement, success story, or testimonial.</p>' },
      ],
    },
  },
  {
    id: 'tpl-deadline-reminder',
    name: 'Enrollment Deadline',
    description: 'Remind contacts about an approaching enrollment deadline.',
    tags: ['Marketing', 'Lead'],
    layout: 'clean',
    content: {
      body: '<p>Hello {{FIRST_NAME}},</p><p>This is a friendly reminder that the enrollment deadline for our upcoming class at {{SCHOOL_NAME}} is approaching. Please make sure to complete all required steps before the deadline to secure your spot.</p><p>If you have any questions or need assistance with the enrollment process, we\'re here to help.</p>',
      button: { text: 'Complete Enrollment', url: '', color: '' },
    },
  },
  {
    id: 'tpl-referral-request',
    name: 'Referral Request',
    description: 'Ask graduates or students to refer friends and family.',
    tags: ['Marketing', 'Student', 'Alumni'],
    layout: 'clean',
    content: {
      body: '<p>Hello {{FIRST_NAME}},</p><p>Know someone who\'s interested in starting a healthcare career? We\'d love to meet them!</p><p>At {{SCHOOL_NAME}}, we\'re always looking for motivated individuals to join our programs. If you know a friend, family member, or colleague who might benefit from our training, please share our information with them.</p><p>Thank you for helping us grow our healthcare community!</p><p>{{SCHOOL_NAME}}</p>',
      button: { text: 'Refer a Friend', url: '', color: '' },
    },
  },
  {
    id: 'tpl-holiday-greeting',
    name: 'Holiday Greeting',
    description: 'Send warm holiday wishes to your contacts.',
    tags: ['General'],
    layout: 'hero',
    content: {
      hero_image: '',
      body: '<p>Hello {{FIRST_NAME}},</p><p>Wishing you and your family a wonderful holiday season from everyone at {{SCHOOL_NAME}}!</p><p>Thank you for being part of our community. We look forward to an exciting new year ahead.</p><p>Warm regards,<br/>The {{SCHOOL_NAME}} Team</p>',
      button: { text: '', url: '', color: '' },
    },
  },
];

// Render Functions

function renderClean(content) {
  var html = '';
  html += '<table border="0" cellpadding="0" cellspacing="0" width="100%">';
  html += '<tr><td style="' + BODY_STYLE + 'padding:0;">';
  html += emailHtml(content.body || '');
  html += '</td></tr>';
  html += '</table>';
  html += renderButton(content.button);
  return html;
}

function renderHero(content) {
  var html = '';
  if (content.hero_image) {
    html += '<table border="0" cellpadding="0" cellspacing="0" width="100%">';
    html += '<tr><td style="padding:0 0 20px 0;">';
    html += '<img src="' + content.hero_image + '" alt="" width="100%" style="display:block;max-width:100%;height:auto;border-radius:8px;" />';
    html += '</td></tr></table>';
  }
  html += '<table border="0" cellpadding="0" cellspacing="0" width="100%">';
  html += '<tr><td style="' + BODY_STYLE + 'padding:0;">';
  html += emailHtml(content.body || '');
  html += '</td></tr>';
  html += '</table>';
  html += renderButton(content.button);
  return html;
}

function renderEvent(content) {
  var html = '';
  if (content.hero_image) {
    html += '<table border="0" cellpadding="0" cellspacing="0" width="100%">';
    html += '<tr><td style="padding:0 0 20px 0;">';
    html += '<img src="' + content.hero_image + '" alt="" width="100%" style="display:block;max-width:100%;height:auto;border-radius:8px;" />';
    html += '</td></tr></table>';
  }
  // Event details card
  html += '<table border="0" cellpadding="0" cellspacing="0" width="100%" style="margin-bottom:20px;background:#f8f9fa;border-radius:8px;">';
  html += '<tr><td style="padding:20px;">';
  if (content.event_name) {
    html += '<div style="' + FONT_STACK + 'font-size:22px;font-weight:bold;color:#202124;margin-bottom:12px;">' + content.event_name + '</div>';
  }
  var details = [];
  if (content.event_date) details.push('📅 ' + content.event_date);
  if (content.event_time) details.push('🕐 ' + content.event_time);
  if (content.event_location) details.push('📍 ' + content.event_location);
  if (details.length) {
    html += '<div style="' + FONT_STACK + 'font-size:15px;color:#5f6368;line-height:1.8;">';
    html += details.join('<br/>');
    html += '</div>';
  }
  html += '</td></tr></table>';
  // Body
  html += '<table border="0" cellpadding="0" cellspacing="0" width="100%">';
  html += '<tr><td style="' + BODY_STYLE + 'padding:0;">';
  html += emailHtml(content.body || '');
  html += '</td></tr></table>';
  html += renderButton(content.button);
  return html;
}

function renderNewsletter(content) {
  var html = '';
  if (content.heading) {
    html += '<table border="0" cellpadding="0" cellspacing="0" width="100%">';
    html += '<tr><td style="' + FONT_STACK + 'font-size:26px;font-weight:bold;color:#202124;padding:0 0 20px 0;">';
    html += content.heading;
    html += '</td></tr></table>';
  }
  var sections = content.sections || [];
  for (var i = 0; i < sections.length; i++) {
    var section = sections[i];
    html += '<table border="0" cellpadding="0" cellspacing="0" width="100%" style="margin-bottom:24px;">';
    if (section.image) {
      html += '<tr><td style="padding:0 0 12px 0;">';
      html += '<img src="' + section.image + '" alt="" width="100%" style="display:block;max-width:100%;height:auto;border-radius:8px;" />';
      html += '</td></tr>';
    }
    if (section.heading) {
      html += '<tr><td style="' + FONT_STACK + 'font-size:20px;font-weight:bold;color:#202124;padding:0 0 8px 0;">';
      html += section.heading;
      html += '</td></tr>';
    }
    html += '<tr><td style="' + BODY_STYLE + 'padding:0;">';
    html += emailHtml(section.body || '');
    html += '</td></tr>';
    // Divider between sections (not after last)
    if (i < sections.length - 1) {
      html += '<tr><td style="padding:12px 0 0 0;">';
      html += '<hr style="border:none;border-top:1px solid #e2e6ea;margin:0;" />';
      html += '</td></tr>';
    }
    html += '</table>';
  }
  return html;
}

function renderWelcome(content) {
  var html = '';
  if (content.heading) {
    html += '<table border="0" cellpadding="0" cellspacing="0" width="100%">';
    html += '<tr><td style="' + FONT_STACK + 'font-size:26px;font-weight:bold;color:#202124;padding:0 0 16px 0;">';
    html += content.heading;
    html += '</td></tr></table>';
  }
  html += '<table border="0" cellpadding="0" cellspacing="0" width="100%">';
  html += '<tr><td style="' + BODY_STYLE + 'padding:0 0 20px 0;">';
  html += emailHtml(content.body || '');
  html += '</td></tr></table>';
  // Highlights list
  var highlights = (content.highlights || []).filter(function (h) { return h && h.trim(); });
  if (highlights.length) {
    html += '<table border="0" cellpadding="0" cellspacing="0" width="100%" style="margin-bottom:20px;">';
    for (var i = 0; i < highlights.length; i++) {
      html += '<tr><td style="' + FONT_STACK + 'font-size:15px;color:#333333;padding:6px 0 6px 0;">';
      html += '✓ ' + highlights[i];
      html += '</td></tr>';
    }
    html += '</table>';
  }
  html += renderButton(content.button);
  return html;
}

// Main Render Dispatcher

export function renderLayout(layoutId, content) {
  switch (layoutId) {
    case 'clean': return renderClean(content);
    case 'hero': return renderHero(content);
    case 'event': return renderEvent(content);
    case 'newsletter': return renderNewsletter(content);
    case 'welcome': return renderWelcome(content);
    default: return '<p>Unknown layout</p>';
  }
}
